"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { getUserPacks, duplicatePack, deletePackWithRag, freePackAllowanceUsed } from "@/platform/firebase/firestore/rfp-packs";
import type { RFPack } from "../types";
import { CATEGORY_OPTIONS, FREE_PACK_LIMIT, type CategoryId } from "../types";
import { isFreePlan } from "../planGate";
import UpgradeDialog from "./UpgradeDialog";
import SharePackDialog from "./SharePackDialog";
import { TourStartButton } from "./PackTour";
import "../pack.css";

export default function PacksDashboard() {
  const router = useRouter();
  const { user, profile } = useAuthContext();
  const [packs, setPacks] = useState<RFPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | PackGradeFilter>("all");
  const [catFilter, setCatFilter] = useState<CategoryId | "all">("all");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [sharePack, setSharePack] = useState<RFPack | null>(null);
  const [allowanceUsed, setAllowanceUsed] = useState(false);

  useEffect(() => {
    if (!user) return;
    getUserPacks(user.uid).then((p) => {
      setPacks(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user]);

  // Authoritative allowance read — flag is persisted on first creation, so
  // deleting the pack here never resets the single free-pack allowance.
  useEffect(() => {
    if (!user) return;
    if (!isFreePlan(profile?.subscription?.plan)) return;
    freePackAllowanceUsed(user.uid).then(setAllowanceUsed).catch(() => {});
  }, [user, profile]);

  const filtered = packs.filter((p) => {
    if (filter !== "all" && p.grade !== filter) return false;
    if (catFilter !== "all" && p.category_id !== catFilter) return false;
    return true;
  });

  const stats = {
    total: packs.length,
    issueReady: packs.filter((p) => p.grade === "issue_ready").length,
    drafts: packs.filter((p) => p.grade === "draft").length,
  };

  // Free tier: exactly one pack allowed for life, no duplication. Deleting a
  // pack does NOT restore the allowance (persisted marker wins over the count).
  const freeAtLimit =
    !loading &&
    isFreePlan(profile?.subscription?.plan) &&
    (allowanceUsed || packs.length >= FREE_PACK_LIMIT);

  const newPackControl = freeAtLimit ? (
    <button
      data-tour="new-pack"
      onClick={() => setShowUpgrade(true)}
      style={{ border: "none", borderRadius: 10, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 650, color: "#fff", background: "linear-gradient(135deg, var(--indigo), var(--magenta))" }}
    >
      Upgrade to Pro
    </button>
  ) : (
    <button
      data-tour="new-pack"
      onClick={() => router.push("/agentic-workflows/rfp-packs/new")}
      style={{ border: "none", borderRadius: 10, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 650, color: "#fff", background: "linear-gradient(135deg, var(--indigo), var(--magenta))" }}
    >
      + New pack
    </button>
  );

  return (
    <>
      <div className="diag-hero">
        <h1 style={{ fontSize: "clamp(28px,4vw,48px)", fontWeight: 800, letterSpacing: "-0.03em", margin: "0 0 8px" }}>
          RFP Packs
        </h1>
        <p className="diag-hero-sub">Comprehensive buy-side sourcing packs — matrix, scorecard, document and gaps, all consistent.</p>
      </div>

      <div className="diag-page" style={{ maxWidth: 1100, paddingTop: 12 }}>
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Total packs", value: stats.total },
            { label: "Issue-ready", value: stats.issueReady },
            { label: "In draft", value: stats.drafts },
          ].map((s) => (
            <div key={s.label} className="pk-stat">
              <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em" }}>{s.value}</div>
              <div style={{ fontSize: 12.5, color: "var(--ink-mute)" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {(["all", "issue_ready", "draft"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`pk-filt ${filter === f ? "on" : ""}`}
            >
              {f === "all" ? "All" : f === "issue_ready" ? "Issue-ready" : "Draft"}
            </button>
          ))}
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value as CategoryId | "all")}
            className="pk-select"
          >
            <option value="all">All categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <div style={{ flex: 1 }} />
          {newPackControl}
          <TourStartButton />
        </div>

        {freeAtLimit && (
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 14, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)" }}>
            <div style={{ flex: 1, textAlign: "left" }}>
              <p style={{ margin: 0, fontWeight: 650, fontSize: 14 }}>Free plan limit reached</p>
              <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--ink-mute)" }}>
                You&apos;ve created your {FREE_PACK_LIMIT} free RFP pack. Upgrade to Pro to generate and download unlimited packs.
              </p>
            </div>
            <button
              onClick={() => setShowUpgrade(true)}
              style={{ flexShrink: 0, border: "none", borderRadius: 10, padding: "9px 18px", fontSize: 13, cursor: "pointer", fontWeight: 650, color: "#fff", background: "linear-gradient(135deg, var(--indigo), var(--magenta))" }}
            >
              Upgrade
            </button>
          </div>
        )}

        {/* List */}
        {loading ? (
          <p style={{ color: "var(--ink-mute)" }}>Loading packs…</p>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--ink-mute)" }}>
            <p>No packs here yet.</p>
            <button
              onClick={() => router.push("/agentic-workflows/rfp-packs/new")}
              style={{ border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 14, cursor: "pointer", fontWeight: 650, color: "#fff", background: "linear-gradient(135deg, var(--indigo), var(--magenta))" }}
            >
              Create your first pack
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((p) => (
              <PackRow key={p.id} pack={p} onOpen={() => router.push(`/agentic-workflows/rfp-packs/${p.id}`)} onAction={async (action) => {
                if (action === "share") {
                  setSharePack(p);
                  return;
                }
                if (!user) return;
                if (action === "duplicate") {
                  if (freeAtLimit) {
                    setShowUpgrade(true);
                    return;
                  }
                  const dup = await duplicatePack(p.id, user.uid, user.email ?? "");
                  router.push(`/agentic-workflows/rfp-packs/${dup.id}`);
                } else if (action === "delete") {
                  await deletePackWithRag(p.id);
                  setPacks((prev) => prev.filter((x) => x.id !== p.id));
                }
              }} />
            ))}
          </div>
        )}
      </div>

      {showUpgrade && (
        <UpgradeDialog
          message="The free plan allows 1 RFP pack. Upgrade to Pro to generate and download unlimited packs."
          onClose={() => setShowUpgrade(false)}
        />
      )}

      {sharePack && (
        <SharePackDialog packId={sharePack.id} onClose={() => setSharePack(null)} />
      )}
    </>
  );
}

type PackGradeFilter = "issue_ready" | "draft";

function PackRow({ pack, onOpen, onAction }: { pack: RFPack; onOpen: () => void; onAction: (a: "share" | "duplicate" | "delete") => void }) {
  const label = CATEGORY_OPTIONS.find((c) => c.id === pack.category_id)?.label ?? "Unknown";
  const tbd = pack.sections.reduce((a, s) => a + s.tbd_count, 0);
  const gapOpen = pack.artifacts.gaps.filter((g) => g.status === "open").length;
  return (
    <div className="pk-card" data-tour="pack-card" style={{ margin: 0, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }} onClick={onOpen}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 650, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pack.name}</div>
        <div style={{ fontSize: 12.5, color: "var(--ink-mute)", marginTop: 2 }}>
          {label} · {pack.client_name} · updated {pack.updated_at ? new Date(pack.updated_at).toLocaleDateString() : "—"}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, fontSize: 12 }}>
          <Chip tone={pack.grade === "issue_ready" ? "green" : "amber"}>{pack.grade === "issue_ready" ? "ISSUE READY" : "DRAFT"}</Chip>
          {tbd > 0 && <Chip tone="amber">{tbd} [TBD]</Chip>}
          {gapOpen > 0 && <Chip tone="red">{gapOpen} gaps open</Chip>}
          {pack.stale && <Chip tone="red">stale</Chip>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          onClick={(e) => { e.stopPropagation(); onAction("share"); }}
          className="pk-btn-ghost-card"
        >
          Share
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onAction("duplicate"); }}
          className="pk-btn-ghost-card"
        >
          Duplicate
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onAction("delete"); }}
          className="pk-btn-danger"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function Chip({ children, tone }: { children: React.ReactNode; tone: "green" | "amber" | "red" }) {
  const map = {
    green: { bg: "rgba(16,185,129,.15)", color: "var(--pk-ok-ink)" },
    amber: { bg: "rgba(245,158,11,.15)", color: "var(--pk-amber-ink)" },
    red: { bg: "rgba(239,68,68,.12)", color: "var(--pk-red)" },
  };
  const t = map[tone];
  return <span style={{ background: t.bg, color: t.color, borderRadius: 999, padding: "2px 9px", fontWeight: 700, fontSize: 11 }}>{children}</span>;
}